import { createContext, useCallback, useEffect, useState } from 'react';
import utilsCities from '../_Utils/utilsCities';

// המקום של המשתמש (server: _routers/citiesRouter.js, DTechComp/geoCities.js).
// cities - המקומות לבחירה (מרחבים + ערים שאינן במרחב). ריק = אין ערים מוגדרות -> האתר מתנהג כמו קודם (בלי בורר).
// city - המקום שנבחר { _id, name, slug, kind, cities: [{ _id, name }], cityNames }. קובע את עמוד הבית (cityId).
// myCity - { _id, name } העיר בפועל בתוך המקום. במקום של עיר אחת - העיר עצמה; במרחב - הלקוח בוחר
//   (או "שתף מיקום"). קובעת אילו עסקים רלוונטיים (DTechComp/placeRelevance.js).
// שניהם נשמרים ב-localStorage לפעמים הבאות.
// pickerOpen - בורר העיר פתוח (DTechComp/CityPicker). פתוח תמיד כשיש ערים ועוד לא נבחר מקום,
//   או שנבחר מרחב בלי עיר (needsMyCity).
// linked - המקום נבחר מכתובת של עמוד מקום (/city/{slug}, DTechComp/CityLanding). מרחב שנבחר כך לא חוסם
//   בשאלת העיר - כל ערי המרחב נחשבות "שלי" עד שהלקוח מדייק. בחירה בבורר מבטלת אותו.
export const CityContext = createContext();

const LS_KEY = 'amCity';

const readStored = () => {
    try {
        const s = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
        return s && s._id ? s : null;
    } catch (e) {
        return null;
    }
};

const writeStored = (city) => {
    try {
        if (city) localStorage.setItem(LS_KEY, JSON.stringify({ _id: city._id, name: city.name, slug: city.slug, myCity: city.myCity || null, linked: !!city.linked }));
        else localStorage.removeItem(LS_KEY);
    } catch (e) { }
};

// פרמטרים לבקשות של רשימות עסקים (server: DTechComp/placeRelevance.js) - השרת מסנן ומסדר לפיהם.
// נקרא מה-localStorage (מסונכרן עם ה-state ב-setCity). myCityId נשלח תמיד (גם null) - כך השרת יודע
// שהבקשה מהאתר ולא מה-ADMIN (homeP)
export const placeParams = () => {
    const s = readStored();
    return { placeId: s ? s._id : null, myCityId: (s && s.myCity && s.myCity._id) || null };
};

// העיר בפועל של מקום:מקום עם עיר אחת - היא; אחרת רק אם היא באמת מערי המקום
const resolveMyCity = (place, myCity) => {
    const list = (place && Array.isArray(place.cities)) ? place.cities : [];
    if (list.length === 1) return list[0];
    if (!myCity) return null;
    return list.find(c => String(c._id) === String(myCity._id)) || null;
};

export const CityContextProvider = props => {
    const [cities, setCities] = useState([]);
    const [city, setCityState] = useState(readStored);
    const [ready, setReady] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        utilsCities.getCities()
            .then(list => {
                if (!alive) return;
                const active = Array.isArray(list) ? list : [];
                setCities(active);
                // מקום שמור שכבר לא פעיל - נבחר מחדש. עיר שמורה שיצאה מהמקום - נבחרת מחדש
                setCityState(prev => {
                    if (!prev) return prev;
                    const fresh = active.find(c => String(c._id) === String(prev._id));
                    if (!fresh) { writeStored(null); return null; }
                    const next = { ...fresh, myCity: resolveMyCity(fresh, prev.myCity), linked: !!prev.linked };
                    writeStored(next);
                    return next;
                });
            })
            .catch(() => { if (alive) setCities([]); })
            .finally(() => { if (alive) setReady(true); });
        return () => { alive = false; };
    }, []);

    // place - המקום; myCity - העיר בפועל (אופציונלי; במרחב בלי עיר הבורר נשאר פתוח לבחירתה)
    // opts.linked - נבחר מכתובת של עמוד מקום (ראו למעלה)
    const setCity = useCallback((place, myCity, opts = {}) => {
        const next = place ? { ...place, myCity: resolveMyCity(place, myCity), linked: !!opts.linked } : null;
        writeStored(next);
        setCityState(next);
        if (!next || next.myCity) setPickerOpen(false);
    }, []);

    const openPicker = useCallback(() => setPickerOpen(true), []);
    const closePicker = useCallback(() => setPickerOpen(false), []);

    // בלי ערים מוגדרות אין מקום בכלל - גם אם נשמר בעבר
    const place = cities.length ? city : null;
    const cityId = place ? place._id : null;
    const myCity = place ? (place.myCity || null) : null;
    // מרחב שנבחר בלי עיר בפועל
    const needsMyCity = !!place && !myCity && !place.linked && Array.isArray(place.cities) && place.cities.length > 1;

    return (
        <CityContext.Provider value={{ cities, city: place, cityId, myCity, needsMyCity, ready, setCity, pickerOpen, openPicker, closePicker }}>
            {props.children}
        </CityContext.Provider>
    );
};
